<template>
  <v-layout>
    <v-flex xs12>
      <child-toolbar :title="title" :buttons="buttons" />
      <v-container grid-list-lg>
        <v-layout row wrap>
          <v-flex xs12>
            <v-subheader>Total {{expenseCount}} Item(s): {{totalExpense | numformat('Rp. ', 0)}}</v-subheader>
          </v-flex>
          <v-flex xs12>
            <v-tabs v-if="expenseCount > 0">
              <template v-for="(category, index) in expenseCategories">
                <v-tab ripple :key="index" :href="`#${category}`">
                  {{category}}<v-chip>{{expensesOfCategory(category).length}}</v-chip>
                </v-tab>
                <v-tab-item :key="`expense-` + index" :id="`${category}`">
                  <v-card>
                    <v-card-text>
                      Total: <strong>{{expensesOfCategory(category) | expensesSum | numformat('Rp. ', 0)}}</strong>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
                <v-tab-item :key="`category-` + index" :id="`${category}`">
                  <v-card>
                    <v-card-text>
                      <v-container grid-list-lg fluid>
                        <v-layout wrap>
                          <v-flex xs12 v-for="expense in expensesOfCategory(category)" :key="expense._tag.v_for_id">
                            <expense-card :value="expense" @delete="_delete"
                                          @edit="edit" :editable="editable" />
                          </v-flex>
                        </v-layout>
                      </v-container>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
              </template>
            </v-tabs>
            <h2 v-else>No expense(s)</h2>
          </v-flex>
        </v-layout>
      </v-container>
    </v-flex>
  </v-layout>
</template>

<script>
import Vuex from 'vuex'
import ExpenseCard from '@/components/business-trip/ExpenseCard'
import ChildToolbar from '@/components/ChildToolbar'
import _ from 'lodash'

const dialogsStore = Vuex.createNamespacedHelpers('Dialogs')

export default {
  name: 'BtRealizationExpense',
  props: {
    value: {
      type: Object,
      required: true
    },
    newExpenseRoute: {
      type: [String, Object],
      required: true
    },
    expenseDetailRoute: {
      type: [String, Object],
      required: true
    },
    editable: {
      type: Boolean,
      required: false,
      default: true
    },
    showGenerateExpense: {
      type: Boolean,
      required: false,
      default: false
    },
    backRoute: {
      type: [String, Object],
      required: false,
      default: null
    }
  },
  components: {
    ExpenseCard,
    ChildToolbar
  },
  data () {
    return {
      title: 'Expense Realizations',
      buttons: [
        { tooltip: 'New Expense Realization',
          icon: 'add',
          to: {
            name: this.newExpenseRoute.name,
            params: {
              backRoute: {
                name: this.$route.name,
                params: {
                  newExpenseRoute: this.newExpenseRoute,
                  expenseDetailRoute: this.expenseDetailRoute,
                  editable: this.editable,
                  showGenerateExpense: this.showGenerateExpense,
                  backRoute: this.backRoute
                }
              }
            }
          },
          render: this.editable
        },
        {
          tooltip: 'Generate Expense',
          icon: 'build',
          handler: this.generateExpense,
          render: this.editable && this.showGenerateExpense
        }
      ],
      expenses: this.value.expenses
    }
  },
  computed: {
    expenseCount () {
      return this.expenses.filter(x => x._tag.status !== 'delete').length
    },
    totalExpense () {
      return this.expenses.filter(x => x._tag.status !== 'delete').map(x => x.subTotal).reduce((x, y) => x + y, 0)
    },
    expenseCategories () {
      const categories = _.uniq(this.expenses.filter(x => x._tag.status !== 'delete')
        .map(x => x.tripExpenseType.replace('_', ' ')))
      return categories.sort()
    }
  },
  watch: {
    'value.expenses' (newValue, oldValue) {
      if (typeof oldValue !== 'undefined') {
        this.expenses = newValue
      }
    }
  },
  methods: {
    ...dialogsStore.mapActions({
      showConfirmation: 'showConfirmation',
      closeConfirmation: 'closeConfirmation'
    }),
    edit (expense) {
      const self = this
      const index = self.expenses.findIndex(x => x._tag.v_for_id === expense._tag.v_for_id)
      self.$router.push({name: self.expenseDetailRoute.name,
        params: {
          index: index,
          expense: expense,
          value: self.value,
          editable: this.editable && expense.editable,
          backRoute: {
            name: this.$route.name,
            params: {
              newExpenseRoute: this.newExpenseRoute,
              expenseDetailRoute: this.expenseDetailRoute,
              editable: this.editable,
              showGenerateExpense: this.showGenerateExpense,
              backRoute: this.backRoute
            }
          }
        }})
    },
    _delete (expense) {
      const self = this

      const yesHandler = () => {
        const delIndex = self.expenses.findIndex(x => x._tag.v_for_id === expense._tag.v_for_id)
        if (expense._tag.status === 'new') {
          self.expenses.splice(delIndex, 1)
        } else {
          self.$set(self.expenses[delIndex]._tag, 'status', 'delete')
        }
        self.$emit('updateExpense', self.expenses)
        self.$error('Business Trip Expense Realization deleted')
        self.closeConfirmation()
      }

      const noHandler = () => {
        self.closeConfirmation()
      }
      self.showConfirmation({
        message: 'Are you sure?',
        title: 'This Business Trip Expense Realization will be deleted.',
        buttons: [{label: 'Yes', handler: yesHandler}, {label: 'No', handler: noHandler}]
      })
    },
    generateExpense () {
      this.$emit('generateExpense')
    },
    expensesOfCategory (category) {
      const expenses = _.sortBy(this.expenses.filter(x => x.tripExpenseType.replace('_', ' ') === category)
        .map((x, index) => {
          if (typeof x._tag.v_for_id === 'undefined') {
            const _tag = _.cloneDeep(x._tag)
            _tag.v_for_id = category + index
            x._tag = _tag
            return x
          } else {
            return x
          }
        }), x => x.tripDate)
      return expenses.filter(x => x._tag.status !== 'delete')
    }
  },
  filters: {
    expensesSum (expenses) {
      return expenses.map(x => x.subTotal).reduce((x, y) => x + y, 0)
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/bt-realization/BtRealizationExpense.vue